/**
 * The visitor directory's state: what it lists (hotspots by folder, and "Views": extra waypoints
 * and the tour waypoints put in it) and which hotspot folders are shown in the scene. Free of engine and DOM objects so it
 * is unit-testable; createViewer applies `hiddenHotspotIds()` to the hotspot markers and draws the
 * panel (directoryPanel.ts) from it.
 *
 * Rules:
 * - Folders form a tree (hotspotFolders.ts), any depth. Hotspots not in a folder come first, then
 *   the folders; inside a folder, its subfolders, then its views, then its hotspots. Folders that
 *   list nothing (in them or below) are left out of the panel.
 * - Every folder starts shown. Each folder has its own flag: a hotspot is hidden when its own
 *   folder's flag is off, on top of its own visibility rules; showing it gives it back exactly the
 *   visibility it would otherwise have. Hotspots not in a folder can't be hidden this way.
 * - Showing or hiding a folder (directory tick box or setHotspotFolderVisible) sets it and every
 *   folder inside it. A folder's tick box sums that up: on, off, or mixed (folderCheckState).
 * - Folder visibility works with or without the directory switched on (scripts may use it); the
 *   directory itself only shows when `visitorDirectory.enabled` is true and it lists something.
 * - Views (directoryViews): extra waypoints unless `hideInDirectory` is set, tour waypoints with
 *   `showInDirectory`, in the creator's `viewOrder`. An extra waypoint filed in a folder is listed
 *   in that folder (same relative order) instead of under Views. Views can't be hidden.
 */
import {
    DEFAULT_DIRECTORY_TITLE,
    directoryViews,
    folderTreeOrder,
    hotspotFolderIdOf,
    groupHotspotsByFolder,
    sanitizeHotspotFolders,
    sanitizeVisitorDirectory,
    visitorDirectoryTitle,
    type DirectoryExtraWaypointSource,
    type DirectoryTourWaypointSource,
    type DirectoryViewEntry,
    type FolderedHotspot,
    type HotspotFoldersConfig,
    type VisitorDirectoryConfig,
} from './hotspotFolders';

/** The fields of a hotspot the directory reads. */
export interface DirectoryHotspotSource extends FolderedHotspot {
    title?: string;
    color?: string;
    type?: string;
    imageUrl?: string;
}

/** The fields of an extra waypoint the directory reads. */
export type DirectoryViewSource = DirectoryExtraWaypointSource;

export interface DirectoryHotspot {
    id: string;
    /** Its title, or "Hotspot n" (its place in the scene order) when it has none. */
    title: string;
    folderId: string | null;
    /** Marker colour (#rrggbb), when it has one. */
    color?: string;
    /** The marker image of an image hotspot (a small thumbnail in the list). */
    imageUrl?: string;
}

export interface DirectoryFolder {
    id: string;
    name: string;
    /** The folder it is in (null: at the top). */
    parentId: string | null;
    /** 0 at the top. */
    depth: number;
    /** Its subfolders in order (empty ones included). */
    children: DirectoryFolder[];
    /** Its own views (extra waypoints filed in it), in the Views order. */
    views: DirectoryView[];
    /** Its own hotspots, in scene order. */
    hotspots: DirectoryHotspot[];
}

/**
 * A View: a tour waypoint (kind 'waypoint', `index` in the tour) or an extra waypoint, with the
 * folder it is filed in (extra waypoints only; absent: listed under Views).
 */
export type DirectoryView = DirectoryViewEntry & { folderId?: string };

/** What a folder's tick box shows: all its hotspots (and those below it) shown, none, or some. */
export type FolderCheckState = 'on' | 'off' | 'mixed';

export interface DirectoryData {
    config?: VisitorDirectoryConfig | null;
    folders?: HotspotFoldersConfig | null;
    hotspots?: readonly DirectoryHotspotSource[] | null;
    extraWaypoints?: readonly DirectoryViewSource[] | null;
    /** Tour waypoints; those with `showInDirectory` are listed under Views. */
    waypoints?: readonly DirectoryTourWaypointSource[] | null;
}

export interface HotspotFolderVisibilityChange {
    id: string;
    visible: boolean;
}

type Listener = (changes: HotspotFolderVisibilityChange[]) => void;

const HEX = /^#[0-9a-f]{6}$/i;

export class VisitorDirectoryState {
    private config: VisitorDirectoryConfig | undefined;
    private unfiled: DirectoryHotspot[] = [];
    /** Every folder in tree order, empty ones included (the API lists them; the panel leaves them out). */
    private folders: DirectoryFolder[] = [];
    private roots: DirectoryFolder[] = [];
    private readonly byId = new Map<string, DirectoryFolder>();
    /** Views not in a folder, and every view. */
    private views: DirectoryView[] = [];
    private allViews: DirectoryView[] = [];
    /** Hotspots and views in each folder's subtree, by folder id. */
    private readonly totals = new Map<string, { hotspots: number; views: number }>();
    /** The folder each filed hotspot is in. */
    private readonly hotspotFolder = new Map<string, string>();
    /** Folder visibility by id; survives data updates for folders that remain. */
    private readonly visible = new Map<string, boolean>();
    private readonly listeners = new Set<Listener>();

    constructor(data?: DirectoryData) {
        if (data) this.setData(data);
    }

    /** Replace what the directory lists (scene load). Folders that remain keep their visibility. */
    setData(data: DirectoryData): void {
        this.config = sanitizeVisitorDirectory(data.config ?? undefined);
        const config = sanitizeHotspotFolders(data.folders ?? undefined);
        const groups = groupHotspotsByFolder(data.hotspots ?? [], config);
        this.byId.clear();
        this.hotspotFolder.clear();
        this.folders = folderTreeOrder(config).map(({ folder, depth }) => {
            const entry: DirectoryFolder = { id: folder.id, name: folder.name, parentId: folder.parentId ?? null, depth, children: [], views: [], hotspots: [] };
            this.byId.set(folder.id, entry);
            return entry;
        });
        this.roots = [];
        for (const folder of this.folders) {
            const parent = folder.parentId ? this.byId.get(folder.parentId) : undefined;
            if (parent) parent.children.push(folder);
            else this.roots.push(folder);
        }
        // "Hotspot n" follows the order visitors see them in: not in a folder, then the folders'.
        let n = 0;
        const entry = (h: DirectoryHotspotSource, folderId: string | null): DirectoryHotspot => {
            n += 1;
            const title = typeof h.title === 'string' && h.title.trim() ? h.title.trim() : `Hotspot ${n}`;
            return {
                id: h.id,
                title,
                folderId,
                ...(typeof h.color === 'string' && HEX.test(h.color) ? { color: h.color } : {}),
                ...(h.type === 'image' && typeof h.imageUrl === 'string' && h.imageUrl ? { imageUrl: h.imageUrl } : {}),
            };
        };
        this.unfiled = groups.unfiled.map((h) => entry(h, null));
        const groupOf = new Map(groups.folders.map((g) => [g.folder.id, g.hotspots]));
        const numberFolder = (folder: DirectoryFolder) => {
            for (const child of folder.children) numberFolder(child);
            folder.hotspots = (groupOf.get(folder.id) ?? []).map((h) => entry(h, folder.id));
            for (const h of folder.hotspots) this.hotspotFolder.set(h.id, folder.id);
        };
        for (const root of this.roots) numberFolder(root);

        const ids = new Set(this.byId.keys());
        const extras = Array.isArray(data.extraWaypoints) ? data.extraWaypoints : [];
        this.allViews = directoryViews(data.waypoints, data.extraWaypoints, this.config?.viewOrder).map((view) => {
            const folderId = view.kind === 'extra' && extras[view.index] ? hotspotFolderIdOf(extras[view.index], ids) : null;
            return folderId ? { ...view, folderId } : view;
        });
        this.views = [];
        for (const view of this.allViews) {
            if (view.folderId) this.byId.get(view.folderId)!.views.push(view);
            else this.views.push(view);
        }

        this.totals.clear();
        for (let i = this.folders.length - 1; i >= 0; i--) {
            const folder = this.folders[i];
            const total = { hotspots: folder.hotspots.length, views: folder.views.length };
            for (const child of folder.children) {
                const t = this.totals.get(child.id)!;
                total.hotspots += t.hotspots;
                total.views += t.views;
            }
            this.totals.set(folder.id, total);
        }

        for (const id of [...this.visible.keys()]) if (!ids.has(id)) this.visible.delete(id);
        for (const id of ids) if (!this.visible.has(id)) this.visible.set(id, true);
        this.emit([]);
    }

    /** The creator turned the directory on. */
    get enabled(): boolean {
        return this.config?.enabled === true;
    }

    get title(): string {
        return visitorDirectoryTitle(this.config);
    }

    get startOpen(): boolean {
        return this.config?.startOpen === true;
    }

    /** Hotspots not in a folder, in scene order. */
    getUnfiled(): DirectoryHotspot[] {
        return this.unfiled;
    }

    /** Every folder in tree order (a folder, then those inside it). `listedOnly` leaves out folders that list nothing, in them or below (what the panel shows). */
    getFolders(listedOnly = false): DirectoryFolder[] {
        return listedOnly ? this.folders.filter((f) => this.lists(f.id)) : this.folders;
    }

    /** The top-level folders in order (`listedOnly`: as in getFolders). Their `children` hold the rest of the tree. */
    getTopFolders(listedOnly = false): DirectoryFolder[] {
        return listedOnly ? this.roots.filter((f) => this.lists(f.id)) : this.roots;
    }

    getFolder(id: string): DirectoryFolder | undefined {
        return this.byId.get(id);
    }

    /** The Views listed under Views (not in a folder), in the creator's order. */
    getViews(): DirectoryView[] {
        return this.views;
    }

    /** Every view the directory lists, in the creator's order, wherever it is listed. */
    getAllViews(): DirectoryView[] {
        return this.allViews;
    }

    /** Every listed hotspot in scene order: not in a folder, then the folders' (subfolders' first). */
    getHotspots(): DirectoryHotspot[] {
        const out = [...this.unfiled];
        const walk = (folder: DirectoryFolder) => {
            for (const child of folder.children) walk(child);
            out.push(...folder.hotspots);
        };
        for (const root of this.roots) walk(root);
        return out;
    }

    /** Hotspots and views in a folder and the folders inside it. */
    countsOf(id: string): { hotspots: number; views: number } {
        return this.totals.get(id) ?? { hotspots: 0, views: 0 };
    }

    /** How many hotspots and views the panel lists. */
    listedCount(): number {
        return this.unfiled.length + this.folders.reduce((sum, f) => sum + f.hotspots.length, 0) + this.allViews.length;
    }

    /** The directory shows: it is on and lists at least one hotspot or view. */
    shouldShow(): boolean {
        return this.enabled && this.listedCount() > 0;
    }

    hasFolder(id: string): boolean {
        return this.visible.has(id);
    }

    /** The folder's own flag: its own hotspots are shown. */
    isFolderVisible(id: string): boolean {
        return this.visible.get(id) !== false;
    }

    /**
     * What a folder's tick box shows: 'on' when every folder in its subtree that holds hotspots is
     * shown, 'off' when none is, 'mixed' otherwise. Null when there are no hotspots to show or hide
     * (no tick box), or for an unknown folder.
     */
    folderCheckState(id: string): FolderCheckState | null {
        const folder = this.byId.get(id);
        if (!folder || !this.countsOf(id).hotspots) return null;
        let on = 0;
        let off = 0;
        const stack = [folder];
        while (stack.length) {
            const next = stack.pop()!;
            if (next.hotspots.length) {
                if (this.visible.get(next.id) === false) off += 1;
                else on += 1;
            }
            stack.push(...next.children);
        }
        return off === 0 ? 'on' : on === 0 ? 'off' : 'mixed';
    }

    /** Ids of the folders shown (their own flag), in tree order. */
    getVisibleFolderIds(): string[] {
        return this.folders.filter((f) => this.visible.get(f.id) !== false).map((f) => f.id);
    }

    /**
     * Show or hide a folder's hotspots and, unless `only`, those of every folder inside it.
     * False for an unknown folder.
     */
    setFolderVisible(id: string, visible: boolean, options: { only?: boolean } = {}): boolean {
        const folder = this.byId.get(id);
        if (!folder) return false;
        const changes: HotspotFolderVisibilityChange[] = [];
        const stack = [folder];
        while (stack.length) {
            const next = stack.pop()!;
            if (this.visible.get(next.id) !== visible) {
                this.visible.set(next.id, visible);
                changes.push({ id: next.id, visible });
            }
            if (!options.only) for (let i = next.children.length - 1; i >= 0; i--) stack.push(next.children[i]);
        }
        if (changes.length) this.emit(changes);
        return true;
    }

    /** Show only this folder and the folders inside it (null: hide every folder). Hotspots not in a folder stay. False for an unknown id. */
    showOnlyFolder(id: string | null): boolean {
        if (id !== null && !this.byId.has(id)) return false;
        const keep = new Set<string>();
        if (id !== null) {
            const stack = [this.byId.get(id)!];
            while (stack.length) {
                const next = stack.pop()!;
                keep.add(next.id);
                stack.push(...next.children);
            }
        }
        const changes: HotspotFolderVisibilityChange[] = [];
        for (const f of this.folders) {
            const next = keep.has(f.id);
            if (this.visible.get(f.id) !== next) {
                this.visible.set(f.id, next);
                changes.push({ id: f.id, visible: next });
            }
        }
        if (changes.length) this.emit(changes);
        return true;
    }

    /** Show every folder. */
    showAllFolders(): void {
        const changes: HotspotFolderVisibilityChange[] = [];
        for (const f of this.folders) {
            if (this.visible.get(f.id) === false) {
                this.visible.set(f.id, true);
                changes.push({ id: f.id, visible: true });
            }
        }
        if (changes.length) this.emit(changes);
    }

    /** Hotspots whose folder is hidden. */
    hiddenHotspotIds(): Set<string> {
        const out = new Set<string>();
        for (const f of this.folders) if (this.visible.get(f.id) === false) for (const h of f.hotspots) out.add(h.id);
        return out;
    }

    /** Whether a hotspot's folder is hidden. */
    isHotspotHidden(hotspotId: string): boolean {
        const folder = this.folderOf(hotspotId);
        return folder !== null && this.visible.get(folder) === false;
    }

    /** The folder a listed hotspot is in (null: none, or not listed). */
    folderOf(hotspotId: string): string | null {
        return this.hotspotFolder.get(hotspotId) ?? null;
    }

    /** Listen for folder visibility changes (none on a data update). Returns the unsubscribe. */
    onChange(listener: Listener): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    destroy(): void {
        this.listeners.clear();
        this.visible.clear();
        this.byId.clear();
        this.totals.clear();
        this.hotspotFolder.clear();
        this.unfiled = [];
        this.folders = [];
        this.roots = [];
        this.views = [];
        this.allViews = [];
    }

    /** The folder lists something, in it or below. */
    private lists(id: string): boolean {
        const t = this.totals.get(id);
        return !!t && t.hotspots + t.views > 0;
    }

    private emit(changes: HotspotFolderVisibilityChange[]): void {
        for (const listener of this.listeners) {
            try {
                listener(changes);
            } catch {
                /* one listener must not stop the others */
            }
        }
    }
}

export { DEFAULT_DIRECTORY_TITLE };
